import * as React from 'react';
import { Button, Col, Layout, Popconfirm, Row } from 'antd';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	compareStrings,
	getObjectValues,
	isHierarchyNTEBased,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import SubnavBar from '../subnav_bar/SubnavBar';
import { fetchFacilityActiveBuyerUsersAndInternalSupplierUsers } from '../../thunks/users_thunks';
import ModalHeaderSteps from '../modal_header_steps_component/modalHeaderSteps';
import {
	approvalsRestCrudThunksForBuyer,
	approvalsRestCrudThunksForSupplier,
	deleteBuyerCompanyApprovalTiers,
	editBuyerCompanyApprovalTiers,
	getBuyerCompanyApprovalTiers,
	patchBuyerCompanyApprovalTiers,
	updateBuyerCompanyApprovalTiers,
} from '../../thunks/company_approval_thunks';
import {
	companyConfigRestCrudThunks,
	companyConfigRestCrudThunksForBuyer,
	companyConfigRestCrudThunksForSupplier,
} from '../../thunks/company_config_thunks';
import { useEffect, useState } from 'react';
import { ROLE_TYPES } from '../../utils/DataConstants';
import { ApprovalHierarchyEditTitle } from './ApprovalHierarchyEditTitle';
import ApprovalHierarchyEditTiers from './ApprovalHierarchyEditTiers';
import { ApprovalHierarchyEditUsers } from './ApprovalHierarchyEditUsers';
import { ApprovalHierarchyPreview } from './ApprovalHierarchyPreview';
import ApprovalHierarchyCopyModal from './ApprovalHierarchyCopyModal';
import { fetchInternalSuppliersAndBuyerUsers } from '../../thunks/work_orders_subscribers_thunks';
import { getApprovalHierarchyDetailLink } from '../../utils/ApprovalHierarchyUtils';

const { Content } = Layout;

const style = require('./ApprovalHierarchyComponent.less');

function ApprovalHierarchyComponent({
	id,
	userType,
	fetchBuyerCompanyApprovalSettings,
	getApprovalHierarchy,
	updateBuyerCompanyApprovalSettings,
	patchBuyerCompanyApprovalSettings,
	editBuyerCompanyApprovalSettings,
	deleteBuyerCompanyApprovalSettings,
	getBuyerCompanyConfig,
	updateBuyerCompanyConfig,
	fetchBuyerUsers,
	fetchUsers,

	currentUser,
	companyConfig,
	token,
	history,
	approvalHierarchies,
	users,

	companyConfigFetching,
	usersFetching,
}) {
	const modalNames = ['Title', 'Tiers', 'Users'];
	const [visibleModal, setVisibleModal] = useState(null);
	const [originalApprovalHierarchy, setOriginalApprovalHierarchy] = useState([]);
	const [editedApprovalHierarchy, setEditedApprovalHierarchy] = useState([]);
	const [copyVisible, setCopyVisible] = useState(false);
	const [initialLoading, setInitialLoading] = useState(false);
	const [isNTEBased, setIsNTEBased] = useState(true);

	const refreshTiers = () => {
		setInitialLoading(true);
		if (id) {
			getApprovalHierarchy(id).then((approvalHierarchy) => {
				setOriginalApprovalHierarchy(approvalHierarchy);
				setInitialLoading(false);
				setIsNTEBased(isHierarchyNTEBased(nullSafeGetOrElse('tierDetails', approvalHierarchy, [])));
			});
		} else {
			setOriginalApprovalHierarchy(null);
			setInitialLoading(false);
		}
	};
	// Keyed on the id, not mount-only: routing from one hierarchy's detail page straight to another
	// (which is what creating a copy does) reuses this component instance, so a mount-only fetch
	// would leave the previous hierarchy on screen under the new id.
	useEffect(() => {
		refreshTiers();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const loading = initialLoading;
	if (loading) {
		return <PageLoadingPlaceholder />;
	}

	const editClick = () => {
		setVisibleModal('Title');

		//Sets edited approval hierarchy to current approval hierarchy to start edit process (while preserving original edit values)
		setEditedApprovalHierarchy(originalApprovalHierarchy);
	};
	const onCancel = () => {
		setVisibleModal(null);
		//Reverts approval hierarchy to original approval hierarchy
		setEditedApprovalHierarchy(originalApprovalHierarchy);
		if (originalApprovalHierarchy) {
			setIsNTEBased(
				isHierarchyNTEBased(nullSafeGetOrElse('tierDetails', originalApprovalHierarchy, []))
			);
		}
	};

	const setApprovalHierarchyName = (name) => {
		setEditedApprovalHierarchy({
			...editedApprovalHierarchy,
			name: name,
		});
	};

	const setApprovalHierarchyTiers = (tiers) => {
		setEditedApprovalHierarchy({
			...editedApprovalHierarchy,
			tierDetails: tiers,
		});
	};

	const formatForAPI = (approvalHierarchy) => {
		const tiers = nullSafeGetOrElse('tierDetails', approvalHierarchy, []).map((tier) => {
			delete tier.id;
			delete tier.showSearch;
			if (!isNTEBased) {
				delete tier.amountUpto;
			}
			return {
				...tier,
				label: nullSafeGetOrElse('label', tier, 0).toString(),
			};
		});

		return {
			...approvalHierarchy,
			tierDetails: tiers,
		};
	};

	const submitApprovalTiers = () => {
		const obj = {
			buyerCompanyId: currentUser.facility.buyerCompanyId,
			...formatForAPI(editedApprovalHierarchy),
		};
		updateBuyerCompanyApprovalSettings(obj).then((result) => {
			setVisibleModal(null);
			history.push(getApprovalHierarchyDetailLink(userType, result.id));
		});
	};

	const updateApprovalTiers = () => {
		const obj = {
			buyerCompanyId: currentUser.facility.buyerCompanyId,
			...formatForAPI(editedApprovalHierarchy),
		};
		editBuyerCompanyApprovalSettings(obj).then(() => {
			setVisibleModal(null);
			refreshTiers();
		});
	};
	const deleteApprovalTiers = (e) => {
		deleteBuyerCompanyApprovalSettings({}, nullSafeGet('id', originalApprovalHierarchy)).then(() =>
			history.goBack()
		);
	};

	const submit = originalApprovalHierarchy === null ? submitApprovalTiers : updateApprovalTiers;

	let steps = [
		{
			title: 'Set Title',
			isActive: compareStrings(visibleModal, 'Title'),
			icon: null,
		},
		{
			title: 'Set Approval Tiers',
			isActive: compareStrings(visibleModal, 'Tiers'),
			icon: null,
		},
		{
			title: 'Add Approvers',
			isActive: compareStrings(visibleModal, 'Users'),
			icon: null,
		},
	];

	let modalHeaderSteps = <ModalHeaderSteps steps={steps} />;

	return (
		<Content className="buyerApprovalSettingsPage">
			<Row type="flex" className="rowSpacing">
				<Col style={{ margin: 'auto' }} span={24}>
					<SubnavBar
						left={<div />}
						right={
							<div>
								{originalApprovalHierarchy && nullSafeGet('id', originalApprovalHierarchy) ? (
									<Button
										size="large"
										style={{ marginRight: 12 }}
										onClick={() => setCopyVisible(true)}
									>
										Make a Copy
									</Button>
								) : null}
								<Button type="primary" size="large" onClick={editClick} style={{ marginRight: 12 }}>
									{originalApprovalHierarchy === null ? 'Create' : 'Edit'}
								</Button>
								{/*originalApprovalHierarchy !== null ? (
                                <Popconfirm
                                    onConfirm={deleteApprovalTiers}
                                    placement="bottomLeft"
                                    title="Are you sure you want to delete your approval tiers?"
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button danger style={{ marginRight: 12 }} size="large" type="primary">
                                        Delete
                                    </Button>
                                </Popconfirm>
                            ) : null*/}
							</div>
						}
					/>
				</Col>
			</Row>
			<ApprovalHierarchyCopyModal
				visible={copyVisible}
				approvalHierarchy={originalApprovalHierarchy}
				userType={userType}
				onCancel={() => setCopyVisible(false)}
				onSuccess={(record) => {
					setCopyVisible(false);
					history.push(getApprovalHierarchyDetailLink(userType, nullSafeGet('id', record)));
				}}
			/>
			<ApprovalHierarchyEditTitle
				visible={compareStrings(visibleModal, 'Title')}
				width={600}
				title={modalHeaderSteps}
				approvalHierarchy={editedApprovalHierarchy}
				setApprovalHierarchyName={setApprovalHierarchyName}
				onNext={() => setVisibleModal('Tiers')}
				onCancel={onCancel}
			/>
			<ApprovalHierarchyEditTiers
				visible={compareStrings(visibleModal, 'Tiers')}
				width={600}
				title={modalHeaderSteps}
				approvalHierarchy={editedApprovalHierarchy}
				setApprovalHierarchyTiers={setApprovalHierarchyTiers}
				onBack={() => setVisibleModal('Title')}
				onNext={() => setVisibleModal('Users')}
				isNTEBased={isNTEBased}
				setIsNTEBased={setIsNTEBased}
			/>
			<ApprovalHierarchyEditUsers
				visible={compareStrings(visibleModal, 'Users')}
				width={600}
				title={modalHeaderSteps}
				approvalHierarchy={editedApprovalHierarchy}
				setApprovalHierarchyTiers={setApprovalHierarchyTiers}
				onBack={() => setVisibleModal('Tiers')}
				onSubmit={submit}
				isNTEBased={isNTEBased}
				currentUser={currentUser}
				userType={userType}
				token={token}
				style={style}
				users={users}
				companyConfigFetching={companyConfigFetching}
				usersFetching={usersFetching}
				getBuyerCompanyConfig={getBuyerCompanyConfig}
				updateBuyerCompanyConfig={updateBuyerCompanyConfig}
				companyConfig={companyConfig}
				fetchBuyerUsers={fetchBuyerUsers}
				fetchUsers={fetchUsers}
			/>
			<ApprovalHierarchyPreview
				approvalHierarchy={originalApprovalHierarchy}
				style={style}
				token={token}
				currentUser={currentUser}
				editClick={editClick}
				isNTEBased={isNTEBased}
			/>
		</Content>
	);
}

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	currentUser: state.session.currentUser,
	token: state.session.token,
	usersFetching: nullSafeGetOrElse('searched_subscribers.loading', state, false),
	users: state.searched_subscribers.records,
	approvalHierarchies: state.approval_hierarchies,
	approvalHierarchiesPatching: state.approval_hierarchies.patching,
	companyConfigFetching: state.company_config.fetching,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	getApprovalHierarchy: (id) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? approvalsRestCrudThunksForSupplier.readOne(id)
				: approvalsRestCrudThunksForBuyer.readOne(id)
		),
	fetchBuyerUsers: (type) =>
		dispatch(fetchFacilityActiveBuyerUsersAndInternalSupplierUsers(ownProps.userType, type)()),
	fetchUsers: () => dispatch(fetchInternalSuppliersAndBuyerUsers('', ownProps.userType, {})()),
	fetchBuyerCompanyApprovalSettings: (id) =>
		dispatch(getBuyerCompanyApprovalTiers(ownProps.userType, id)()),
	updateBuyerCompanyApprovalSettings: (tiers) =>
		dispatch(updateBuyerCompanyApprovalTiers(ownProps.userType, tiers)()),
	editBuyerCompanyApprovalSettings: (tiers) =>
		dispatch(editBuyerCompanyApprovalTiers(ownProps.userType, tiers)()),
	deleteBuyerCompanyApprovalSettings: (tiers, id) =>
		dispatch(deleteBuyerCompanyApprovalTiers(ownProps.userType, tiers, id)()),
	patchBuyerCompanyApprovalSettings: (id, mirrorId, type) =>
		dispatch(patchBuyerCompanyApprovalTiers(ownProps.userType, id, mirrorId, type)()),
	getBuyerCompanyConfig: (contact) =>
		dispatch(companyConfigRestCrudThunks(ownProps.userType, contact)),
	updateBuyerCompanyConfig: (settings) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? companyConfigRestCrudThunksForSupplier.update(settings)
				: companyConfigRestCrudThunksForBuyer.update(settings)
		),
});

const ApprovalHierarchyComponentWR = withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ApprovalHierarchyComponent)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ApprovalHierarchyComponentWR);
